<template>
    <RegularModal ref="modal" class="bulk-delete-brackets-modal" @closed="clearSelection">
        <template v-slot:title>
            <h4 v-if="phase === 'confirm'">
                {{ translate("admin_bracket_listing.bulk_delete_confirm_title", false, [selectedCount]) }}
            </h4>
            <h4 v-else>
                {{ translate("common.bulk_delete_result_title") }}
            </h4>
        </template>
        <template v-slot:body>
            <template v-if="phase === 'confirm'">
                <p>{{ translate("admin_bracket_listing.bulk_delete_confirm_warning", false, [selectedCount]) }}</p>
                <SelectionList :options="selectedBrackets" readonly height="240px" class="mt-3">
                    <template v-slot:option="{ option: bracket }">
                        <span>{{ bracket.name }}</span>
                    </template>
                </SelectionList>
            </template>
            <template v-else>
                <p>
                    {{
                        translate("admin_bracket_listing.bulk_delete_result_summary", false, [
                            result?.deletedParameterIds.length ?? 0,
                            selectedCount,
                        ])
                    }}
                </p>
                <template v-if="result && result.skipped.length > 0">
                    <p class="has-text-weight-semibold mt-4">
                        {{ translate("admin_bracket_listing.bulk_delete_result_skipped_intro") }}
                    </p>
                    <SelectionList :options="result.skipped" readonly height="240px" class="mt-2">
                        <template v-slot:option="{ option: skippedItem }">
                            <span class="has-text-white">{{ bracketName(skippedItem.parameterId) }}</span>
                            <span class="has-text-grey-light"> — {{ translate(skippedItem.reason) }}</span>
                        </template>
                    </SelectionList>
                </template>
            </template>
        </template>
        <template v-slot:footer>
            <div class="buttons" v-if="phase === 'confirm'">
                <button class="button" @click="close">
                    {{ translate("common.cancel") }}
                </button>
                <LoadingButton class="button is-danger" :is-loading="deleting" @click="confirmDelete">
                    <span class="icon"><i class="fa fa-trash"></i></span>
                    <span>{{ translate("common.delete") }}</span>
                </LoadingButton>
            </div>
            <div class="buttons" v-else>
                <button class="button is-primary" @click="close">
                    {{ translate("common.close") }}
                </button>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref, computed } from "vue";
    import RegularModal from "../../Common/RegularModal.vue";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import SelectionList from "../../Common/SelectionList.vue";
    import { useModal } from "../../Common/useModal";
    import { translate } from "../../Common/translations";
    import { handleError } from "../../Common/errorHandler";
    import { successToast } from "../../Common/toasts";
    import bracketsApi, { BulkDeleteBracketsResult } from "../../Api/brackets";
    import { useBracketsListingStore } from "./bracketsListingApp";

    const props = defineProps<{
        eventId: number;
    }>();

    const { modal, open: openModal, close: closeModal } = useModal();
    const store = useBracketsListingStore();

    const phase = ref<"confirm" | "result">("confirm");
    const deleting = ref(false);
    const result = ref<BulkDeleteBracketsResult | null>(null);
    const selectedParameterIds = ref<number[]>([]);

    const selectedCount = computed(() => selectedParameterIds.value.length);

    const selectedBrackets = computed(() =>
        selectedParameterIds.value
            .map(parameterId => store.complexBrackets.find(cb => cb.parameter_id === parameterId))
            .filter(bracket => bracket !== undefined)
            .sort((a, b) => a.name.localeCompare(b.name)),
    );

    function bracketName(parameterId: number): string {
        return store.complexBrackets.find(cb => cb.parameter_id === parameterId)?.name ?? "";
    }

    function confirmDelete() {
        deleting.value = true;
        bracketsApi
            .bulkDeleteBrackets(props.eventId, selectedParameterIds.value)
            .then(response => {
                result.value = response;
                phase.value = "result";
                successToast(
                    translate("admin_bracket_listing.bulk_delete_result_summary", false, [
                        response.deletedParameterIds.length,
                        selectedCount.value,
                    ]),
                );
                return store.refreshBracketsListing();
            })
            .catch(handleError)
            .finally(() => {
                deleting.value = false;
            });
    }

    function open() {
        phase.value = "confirm";
        deleting.value = false;
        result.value = null;
        selectedParameterIds.value = store.selectedParameterIdsList;
        openModal();
    }

    function close() {
        closeModal();
    }

    function clearSelection() {
        store.clearSelection();
    }

    defineExpose({ open });
</script>
